/**
 * Crown01 unified analytics layer (consent-aware)
 *
 * - GA4 (Google) — analytics category
 * - Meta Pixel — marketing category (activates when REACT_APP_META_PIXEL_ID is set)
 * - TikTok Pixel — marketing category (activates when REACT_APP_TIKTOK_PIXEL_ID is set)
 *
 * Pixels are only injected when the user's stored consent allows the matching
 * category. Tracker helpers (trackPageView, trackViewItem, ...) safely no-op if
 * the underlying pixel hasn't been loaded.
 */

const GA4_ID = process.env.REACT_APP_GA4_ID || "";
const META_PIXEL_ID = process.env.REACT_APP_META_PIXEL_ID || "";
const TIKTOK_PIXEL_ID = process.env.REACT_APP_TIKTOK_PIXEL_ID || "";

const CONSENT_KEY = "crown01_cookie_consent";
const CONSENT_VERSION = 1;

let _gaLoaded = false;
let _metaLoaded = false;
let _ttLoaded = false;

// ---------------------- consent storage --------------------------------------

/** Returns the user's stored consent, or null if they have not made a choice. */
export function getConsent() {
  try {
    const raw = localStorage.getItem(CONSENT_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    if (parsed.version !== CONSENT_VERSION) return null;
    return parsed;
  } catch { return null; }
}

/** Persist a consent decision. Pass {analytics: bool, marketing: bool}. */
export function setConsent({ analytics = false, marketing = false } = {}) {
  const payload = {
    necessary: true,
    analytics: !!analytics,
    marketing: !!marketing,
    version: CONSENT_VERSION,
    ts: new Date().toISOString(),
  };
  try { localStorage.setItem(CONSENT_KEY, JSON.stringify(payload)); } catch { /* noop */ }
  // Initialise any newly-enabled pixels right away.
  initAnalytics();
  return payload;
}

function _consent() {
  return getConsent() || { necessary: true, analytics: false, marketing: false };
}

// ---------------------- pixel injectors --------------------------------------

function _injectGA4() {
  if (_gaLoaded || !GA4_ID) return;
  _gaLoaded = true;
  const s = document.createElement("script");
  s.async = true;
  s.src = `https://www.googletagmanager.com/gtag/js?id=${GA4_ID}`;
  document.head.appendChild(s);
  window.dataLayer = window.dataLayer || [];
  window.gtag = function () { window.dataLayer.push(arguments); };
  window.gtag("js", new Date());
  window.gtag("config", GA4_ID, {
    send_page_view: false, // we send page_view manually on route change
    anonymize_ip: true,
    cookie_flags: "SameSite=None;Secure",
  });
}

function _injectMetaPixel() {
  if (_metaLoaded || !META_PIXEL_ID) return;
  _metaLoaded = true;
  /* eslint-disable */
  !function(f,b,e,v,n,t,s){if(f.fbq)return;n=f.fbq=function(){n.callMethod?
    n.callMethod.apply(n,arguments):n.queue.push(arguments)};if(!f._fbq)f._fbq=n;
    n.push=n;n.loaded=!0;n.version='2.0';n.queue=[];t=b.createElement(e);t.async=!0;
    t.src=v;s=b.getElementsByTagName(e)[0];s.parentNode.insertBefore(t,s)}(window,
    document,'script','https://connect.facebook.net/en_US/fbevents.js');
  /* eslint-enable */
  window.fbq("init", META_PIXEL_ID);
}

function _injectTikTokPixel() {
  if (_ttLoaded || !TIKTOK_PIXEL_ID) return;
  _ttLoaded = true;
  /* eslint-disable */
  !function (w, d, t) {
    w.TiktokAnalyticsObject=t;
    var ttq=w[t]=w[t]||[];
    ttq.methods=["page","track","identify","instances","debug","on","off","once","ready","alias","group","enableCookie","disableCookie"];
    ttq.setAndDefer=function(t,e){t[e]=function(){t.push([e].concat(Array.prototype.slice.call(arguments,0)))}};
    for(var i=0;i<ttq.methods.length;i++)ttq.setAndDefer(ttq,ttq.methods[i]);
    ttq.instance=function(t){for(var e=ttq._i[t]||[],n=0;n<ttq.methods.length;n++)ttq.setAndDefer(e,ttq.methods[n]);return e};
    ttq.load=function(e,n){var r="https://analytics.tiktok.com/i18n/pixel/events.js";ttq._i=ttq._i||{};ttq._i[e]=[];ttq._i[e]._u=r;ttq._t=ttq._t||{};ttq._t[e]=+new Date;ttq._o=ttq._o||{};ttq._o[e]=n||{};var o=document.createElement("script");o.type="text/javascript";o.async=!0;o.src=r+"?sdkid="+e+"&lib="+t;var a=document.getElementsByTagName("script")[0];a.parentNode.insertBefore(o,a)};
    ttq.load(TIKTOK_PIXEL_ID);
    ttq.page();
  }(window, document, 'ttq');
  /* eslint-enable */
}

export function initAnalytics() {
  const c = _consent();
  if (c.analytics) {
    try { _injectGA4(); } catch (e) { console.warn("GA4 init failed", e); }
  }
  if (c.marketing) {
    try { _injectMetaPixel(); } catch (e) { console.warn("Meta Pixel init failed", e); }
    try { _injectTikTokPixel(); } catch (e) { console.warn("TikTok Pixel init failed", e); }
  }
}

// ---------------------- low-level senders (silent on missing pixel) ----------

function _ga(event, params = {}) {
  if (!_consent().analytics) return;
  if (!GA4_ID || typeof window.gtag !== "function") return;
  try { window.gtag("event", event, params); } catch (e) { /* noop */ }
}

function _meta(event, params = {}, type = "track") {
  if (!_consent().marketing) return;
  if (!META_PIXEL_ID || typeof window.fbq !== "function") return;
  try { window.fbq(type, event, params); } catch (e) { /* noop */ }
}

function _tt(event, params = {}) {
  if (!_consent().marketing) return;
  if (!TIKTOK_PIXEL_ID || typeof window.ttq?.track !== "function") return;
  try { window.ttq.track(event, params); } catch (e) { /* noop */ }
}

// ---------------------- public, intent-based events --------------------------

export function trackPageView(path, title) {
  _ga("page_view", {
    page_location: window.location.href,
    page_path: path,
    page_title: title,
  });
  _meta("PageView");
  if (_consent().marketing && TIKTOK_PIXEL_ID && typeof window.ttq?.page === "function") {
    try { window.ttq.page(); } catch (e) {}
  }
}

export function trackViewItem(p, variant) {
  if (!p) return;
  const price = variant?.price ?? p.min_price ?? 0;
  const currency = p.currency || "GBP";
  _ga("view_item", {
    currency, value: price,
    items: [{
      item_id: variant?.id || p.id, item_name: p.name,
      item_category: p.category, item_variant: variant?.title, price,
    }],
  });
  _meta("ViewContent", { content_ids: [variant?.id || p.id], content_name: p.name, content_type: "product", value: price, currency });
  _tt("ViewContent", { contents: [{ content_id: variant?.id || p.id, content_name: p.name, price }], value: price, currency });
}

export function trackAddToCart(p, variant, qty = 1) {
  const price = (variant?.price ?? 0) * qty;
  const currency = p?.currency || "GBP";
  _ga("add_to_cart", {
    currency, value: price,
    items: [{
      item_id: variant?.id, item_name: p?.name,
      item_category: p?.category, item_variant: variant?.title, quantity: qty, price: variant?.price,
    }],
  });
  _meta("AddToCart", { content_ids: [variant?.id], content_name: p?.name, content_type: "product", value: price, currency });
  _tt("AddToCart", { contents: [{ content_id: variant?.id, content_name: p?.name, quantity: qty, price: variant?.price }], value: price, currency });
}

export function trackBeginCheckout(items, subtotal) {
  const currency = "GBP";
  _ga("begin_checkout", {
    currency, value: subtotal,
    items: (items || []).map(i => ({
      item_id: i.variant_id, item_name: i.name,
      item_variant: i.variant_title, quantity: i.qty, price: i.unit_price,
    })),
  });
  _meta("InitiateCheckout", {
    content_ids: (items || []).map(i => i.variant_id),
    contents: (items || []).map(i => ({ id: i.variant_id, quantity: i.qty })),
    value: subtotal, currency, num_items: (items || []).reduce((s, i) => s + i.qty, 0),
  });
  _tt("InitiateCheckout", {
    contents: (items || []).map(i => ({ content_id: i.variant_id, content_name: i.name, quantity: i.qty, price: i.unit_price })),
    value: subtotal, currency,
  });
}

export function trackSearch(query) {
  if (!query) return;
  _ga("search", { search_term: query });
  _meta("Search", { search_string: query });
  _tt("Search", { query });
}

export function trackSignUp(method = "newsletter") {
  _ga("sign_up", { method });
  _meta("CompleteRegistration", { content_name: method });
  _tt("CompleteRegistration");
}

export function trackLead(form_name = "contact") {
  _ga("generate_lead", { form_name });
  _meta("Lead", { content_name: form_name });
  _tt("SubmitForm", { content_id: form_name });
}
